import { useMemo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  PlusIcon,
  Edit02Icon,
  Trash01Icon,
  AlertCircleIcon,
  SearchMdIcon,
} from '@untitledui-icons/react/line'
import type { SortDescriptor } from 'react-aria-components'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { EmptyState } from '@/components/common'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import {
  usePromptTemplates,
  useDeletePromptTemplate,
  type PromptTemplate,
} from '@/hooks/api/usePromptTemplates'
import { usePermissions } from '@/hooks/api/usePermissions'
import { useIdentity } from '@/hooks/api/useIdentity'
import { useCustomersByIds } from '@/hooks/api/useCustomers'
import { useListPageSearch } from '@/hooks/useListPageSearch'

const PAGE_SIZE = 25

interface PromptTemplateFilters {
  q?: string
  name?: string
}

export function PromptTemplatesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { can, role } = usePermissions()

  // Search and pagination with stable callbacks (prevents focus loss)
  // Note: searchQuery is local state for immediate UI feedback
  // filters is derived from URL params and only updates after debounce
  const {
    searchQuery,
    handleSearch,
    filters: urlFilters,
    page,
    handlePageChange,
  } = useListPageSearch<PromptTemplateFilters>({
    useJsonFilter: false,
    paramKey: 'q',
    debounceMs: 300,
    defaultPerPage: PAGE_SIZE,
  })

  // Local state
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'created_at',
    direction: 'descending',
  })
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplate | null>(null)

  // Derived sort params for API
  const sort = useMemo(
    () => ({
      field: sortDescriptor.column as string,
      order: (sortDescriptor.direction === 'descending' ? 'DESC' : 'ASC') as 'ASC' | 'DESC',
    }),
    [sortDescriptor]
  )

  // Build filter from URL params (debounced) - NOT from local searchQuery
  const filter = useMemo(() => {
    const f: Record<string, unknown> = {}
    if (urlFilters.q) {
      f.name = `%${urlFilters.q}%`
    }
    return f
  }, [urlFilters.q])

  // Fetch templates
  const {
    data: templatesData,
    isLoading,
    isError,
  } = usePromptTemplates({
    page,
    perPage: PAGE_SIZE,
    sort,
    filter,
  })

  const templates = templatesData?.data || []
  const totalCount = templatesData?.total || 0

  // Get customer IDs for reference display
  const customerIds = useMemo(
    () => [...new Set(templates.filter((t) => t.customer_id).map((t) => t.customer_id!))],
    [templates]
  )
  const { data: customers } = useCustomersByIds(customerIds)
  const customerMap = useMemo(() => {
    const map = new Map<number, string>()
    customers?.forEach((c) => map.set(c.id, c.name))
    return map
  }, [customers])

  // Delete mutation
  const deleteTemplate = useDeletePromptTemplate()

  // Handlers
  const handleEdit = useCallback(
    (template: PromptTemplate) => {
      navigate(`/prompt-templates/${template.id}`)
    },
    [navigate]
  )

  const handleDelete = async () => {
    if (!deleteTarget) return

    try {
      await deleteTemplate.mutateAsync(deleteTarget.id)
      toast.success(t('resources.prompt_templates.delete_success'))
      setDeleteTarget(null)
    } catch (error: unknown) {
      if (error instanceof Error && error.message.includes('409')) {
        toast.warning(t('resources.prompt_templates.in_use'))
      } else {
        const message =
          error instanceof Error ? error.message : t('resources.prompt_templates.delete_error')
        toast.error(message)
      }
    }
  }

  // Fetch identity for customer meta check
  const { user: identityUser } = useIdentity()

  // Check access - admin, partner, or user with allowed_to_make_templates
  const isAdmin = role === 'admin'
  const isPartner = role === 'partner'
  const isUser = role === 'user'
  const userHasTemplateAccess =
    isUser && identityUser?.customer?.meta?.allowed_to_make_templates === true
  const hasAccess = can('view:prompt-templates') || userHasTemplateAccess

  // Handle sort change
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  // Show customer scope column for admin/partner
  const showCustomerScope = isAdmin || isPartner

  // Check if filters are active
  const hasActiveFilters = !!searchQuery

  // Access denied state
  if (!hasAccess) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={<AlertCircleIcon className="h-12 w-12 text-gray-400" />}
          title={t('errors.access_denied')}
          description={t('resources.prompt_templates.access_denied')}
        />
      </div>
    )
  }

  // Initial loading state - only show full spinner when we have no data yet and no filters
  const showInitialLoading = isLoading && !templatesData && !hasActiveFilters

  if (showInitialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Error state
  if (isError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={<AlertCircleIcon className="h-12 w-12 text-error-400" />}
          title={t('resources.prompt_templates.load_error')}
          description={t('resources.prompt_templates.load_error_description')}
        />
      </div>
    )
  }

  // Simple empty state (no data and no filters)
  if (!templatesData && !hasActiveFilters && templates.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          icon={<AlertCircleIcon className="h-12 w-12 text-gray-400" />}
          title={t('resources.prompt_templates.empty')}
          description={t('resources.prompt_templates.empty_description')}
          action={
            <Button
              color="primary"
              onClick={() => navigate('/prompt-templates/create')}
              iconLeading={PlusIcon}
            >
              {t('resources.prompt_templates.create')}
            </Button>
          }
        />
      </div>
    )
  }

  // Unified table layout - always render the same structure to preserve focus
  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('resources.prompt_templates.overview')}
        description={t('resources.prompt_templates.subtitle')}
        contentTrailing={
          <div className="flex items-center gap-3">
            {/* Search */}
            <Input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder={t('resources.prompt_templates.search_placeholder')}
              leftIcon={<SearchMdIcon className="h-5 w-5" />}
              className="w-40 sm:w-56"
            />

            {/* Create button */}
            <Button color="primary" onClick={() => navigate('/prompt-templates/create')} iconLeading={PlusIcon}>
              <span className="hidden sm:inline">{t('resources.prompt_templates.create')}</span>
            </Button>
          </div>
        }
      />

      {/* Info banner */}
      <div className="border-b border-gray-200 bg-brand-50 px-4 py-3 dark:border-gray-700 dark:bg-brand-950 sm:px-6">
        <p className="text-sm text-brand-700 dark:text-brand-300">
          {t('app.prompt_templates.info')}
        </p>
      </div>

      {/* Content area - either table or empty state */}
      {templates.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <EmptyState
            icon={<AlertCircleIcon className="h-12 w-12 text-gray-400" />}
            title={t('resources.prompt_templates.empty')}
            description={t('resources.prompt_templates.empty_search')}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <Table
            aria-label={t('resources.prompt_templates.overview')}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          >
            <Table.Header>
              <Table.Head id="id" label="ID" allowsSorting isRowHeader />
              <Table.Head id="name" label={t('resources.prompt_templates.fields.name')} allowsSorting />
              <Table.Head id="description" label={t('resources.prompt_templates.fields.description')} />
              {showCustomerScope && (
                <Table.Head id="customer_id" label={t('resources.prompt_templates.fields.customer_scope')} />
              )}
              <Table.Head id="created_at" label={t('resources.prompt_templates.fields.created_at')} allowsSorting />
              <Table.Head id="actions" label="" />
            </Table.Header>

            <AriaTableBody>
              {templates.map((template) => (
                <Table.Row
                  key={template.id}
                  id={template.id}
                  className="cursor-pointer"
                  onAction={() => handleEdit(template)}
                >
                  {/* ID */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{template.id}</span>
                  </Table.Cell>

                  {/* Name */}
                  <Table.Cell>
                    <span className="text-gray-900 dark:text-gray-100">{template.name}</span>
                  </Table.Cell>

                  {/* Description */}
                  <Table.Cell>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {template.description || '-'}
                    </span>
                  </Table.Cell>

                  {/* Customer Scope (admin/partner only) */}
                  {showCustomerScope && (
                    <Table.Cell>
                      {template.customer_id ? (
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {customerMap.get(template.customer_id) || `${t('resources.customers.name', { count: 1 })} ${template.customer_id}`}
                        </span>
                      ) : (
                        <Badge variant="gray">{t('resources.prompt_templates.global')}</Badge>
                      )}
                    </Table.Cell>
                  )}

                  {/* Created At */}
                  <Table.Cell>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {format(new Date(template.created_at), 'MMM d, yyyy HH:mm')}
                    </span>
                  </Table.Cell>

                  {/* Actions */}
                  <Table.Cell>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEdit(template)
                        }}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                        title={t('resources.generic.edit')}
                      >
                        <Edit02Icon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(template)
                        }}
                        className="rounded p-1 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:hover:bg-error-950 dark:hover:text-error-400"
                        title={t('resources.generic.delete')}
                      >
                        <Trash01Icon className="h-4 w-4" />
                      </button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </AriaTableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalCount > PAGE_SIZE && (
        <PaginationTable
          page={page}
          total={Math.ceil(totalCount / PAGE_SIZE)}
          perPage={PAGE_SIZE}
          onPageChange={handlePageChange}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('resources.prompt_templates.delete_title')}
        description={t('resources.prompt_templates.delete_description', { name: deleteTarget?.name })}
        confirmLabel={t('resources.generic.delete')}
        variant="error"
        isLoading={deleteTemplate.isPending}
      />
    </TableCard.Root>
  )
}

export default PromptTemplatesPage
